// ─── Doc Types ───────────────────────────────────────────────────────────────

export const DOC_TYPES = [
  "adr",
  "prd",
  "rfc",
  "tdd",
  "runbook",
  "postmortem",
  "slo",
  "service-catalog",
  "deprecation",
  "tech-debt",
  "api-contract",
  "feature-brief",
  "decision-log",
  "roadmap-snapshot",
  "quarterly-review",
  "onboarding",
] as const;

export type DocType = (typeof DOC_TYPES)[number];

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  adr: "ADR",
  prd: "PRD",
  rfc: "RFC",
  tdd: "TDD",
  runbook: "Runbook",
  postmortem: "Post-mortem",
  slo: "SLO",
  "service-catalog": "Service Catalog",
  deprecation: "Deprecation",
  "tech-debt": "Tech Debt",
  "api-contract": "API Contract",
  "feature-brief": "Feature Brief",
  "decision-log": "Decision Log",
  "roadmap-snapshot": "Roadmap Snapshot",
  "quarterly-review": "Quarterly Review",
  onboarding: "Onboarding",
};

export const DOC_TYPE_PREFIXES: Record<string, DocType> = {
  adr: "adr",
  prd: "prd",
  rfc: "rfc",
  tdd: "tdd",
  run: "runbook",
  pm: "postmortem",
  slo: "slo",
  svc: "service-catalog",
  dep: "deprecation",
  td: "tech-debt",
  api: "api-contract",
  fb: "feature-brief",
  dl: "decision-log",
  rm: "roadmap-snapshot",
  qr: "quarterly-review",
  ob: "onboarding",
};

// ─── Review Frequency ────────────────────────────────────────────────────────

export type ReviewFrequency =
  | "none"
  | "monthly"
  | "quarterly"
  | "annually"
  | "on-change";

// ─── Visibility ──────────────────────────────────────────────────────────────

export type Visibility = "private" | "team" | "org" | "public";

// ─── Sync Source ─────────────────────────────────────────────────────────────

export type SyncSource = "claude" | "human" | "platform-sync" | "ci" | "import";

// ─── Tasks (cross-doc work items) ────────────────────────────────────────────

export type TaskStatus = "open" | "in-progress" | "done" | "wont-fix";

export interface Task {
  item: string;
  owner?: string;
  due?: string;
  status: TaskStatus;
  related?: string | null;
}

// ─── Base Schema ─────────────────────────────────────────────────────────────

export interface BaseFrontmatter {
  // Identity
  id: string;
  type: DocType;
  title: string;
  slug?: string;

  // Status
  status: string;

  // Ownership
  author?: string;
  owner?: string;
  reviewers?: string[];
  approvers?: string[];

  // Timing
  created?: string;
  updated?: string;
  reviewed?: string | null;
  review_frequency?: ReviewFrequency;
  next_review?: string | null;

  // Relationships
  related?: string[];
  supersedes?: string[];
  superseded_by?: string | null;
  implements?: string[];
  motivated_by?: string[];
  part_of?: string[];
  depends_on?: string[];

  // Scope
  tags?: string[];
  domain?: string;
  services?: string[];
  products?: string[];

  // Tasks (work items associated with this doc)
  tasks?: Task[];

  // Platform / Sync
  platform_id?: string | null;
  visibility?: Visibility;
  sync_source?: SyncSource;
  source_hash?: string | null;
  generated_by?: string | null;
}

// ─── Type-Specific Extensions ────────────────────────────────────────────────

export interface AdrFrontmatter extends BaseFrontmatter {
  type: "adr";
  decision_date?: string;
  decision_makers?: string[];
  problem_statement?: string;
  decision_summary?: string;
  context_snapshot?: {
    scale?: string;
    team_size?: number;
    constraints?: string[];
  };
  alternatives_considered?: Array<{
    id?: string | null;
    name: string;
    reason_rejected: string;
  }>;
  trade_offs?: string[];
  assumptions?: string[];
  invalidation_triggers?: string[];
  consequences?: {
    positive?: string[];
    negative?: string[];
    neutral?: string[];
  };
}

export interface PrdFrontmatter extends BaseFrontmatter {
  type: "prd";
  product?: string;
  feature?: string;
  priority?: "p0" | "p1" | "p2" | "p3";
  target_release?: string;
  stakeholders?: string[];
  success_metrics?: string[];
  out_of_scope?: string[];
  dependencies?: string[];
}

export interface RfcFrontmatter extends BaseFrontmatter {
  type: "rfc";
  rfc_number?: number;
  stage?: string;
  discussion_deadline?: string;
  voting_required?: boolean;
  votes?: {
    approve?: string[];
    reject?: string[];
    abstain?: string[];
  };
  open_questions?: string[];
}

export interface TddFrontmatter extends BaseFrontmatter {
  type: "tdd";
  services_affected?: string[];
  tech_stack?: string[];
  estimated_complexity?: "xs" | "s" | "m" | "l" | "xl";
  data_flows?: string[];
  open_decisions?: string[];
}

export interface RunbookFrontmatter extends BaseFrontmatter {
  type: "runbook";
  service?: string;
  runbook_type?:
    | "deployment"
    | "rollback"
    | "scaling"
    | "incident"
    | "maintenance"
    | "debug";
  severity_applicability?: "sev1" | "sev2" | "sev3" | "routine";
  estimated_duration?: string;
  prerequisites?: string[];
  oncall_contact?: string;
  last_tested?: string;
}

export interface PostmortemFrontmatter extends BaseFrontmatter {
  type: "postmortem";
  incident_id?: string;
  severity?: "sev1" | "sev2" | "sev3";
  incident_date?: string;
  duration?: string;
  services_affected?: string[];
  contributing_factors?: string[];
  action_items?: Array<{
    item: string;
    owner: string;
    due: string;
    status: "open" | "in-progress" | "done" | "wont-fix";
    adr?: string | null;
  }>;
  follow_up_adrs?: string[];
}

export interface SloFrontmatter extends BaseFrontmatter {
  type: "slo";
  service?: string;
  slo_type?:
    | "availability"
    | "latency"
    | "error_rate"
    | "throughput"
    | "custom";
  target?: string;
  measurement_window?: string;
  error_budget?: string;
  alerting_threshold?: string;
  measurement_method?: string;
  consequences_of_breach?: string;
}

export interface ServiceCatalogFrontmatter extends BaseFrontmatter {
  type: "service-catalog";
  service_name?: string;
  service_type?:
    | "api"
    | "worker"
    | "frontend"
    | "database"
    | "infra"
    | "library"
    | "cli";
  tier?: "critical" | "important" | "standard" | "experimental";
  repo?: string;
  health_endpoint?: string;
  oncall?: string;
  tech_stack?: string[];
  depends_on_services?: string[];
  consumed_by?: string[];
  runbook?: string;
  slo?: string;
}

export interface DeprecationFrontmatter extends BaseFrontmatter {
  type: "deprecation";
  target?: string;
  target_type?:
    | "service"
    | "api"
    | "endpoint"
    | "library"
    | "feature"
    | "config";
  deprecation_date?: string;
  eol_date?: string;
  migration_path?: string;
  migration_guide?: string;
  affected_consumers?: string[];
  contact?: string;
  breaking_changes?: string[];
}

export interface TechDebtFrontmatter extends BaseFrontmatter {
  type: "tech-debt";
  debt_type?:
    | "architectural"
    | "code-quality"
    | "test-coverage"
    | "documentation"
    | "security"
    | "performance"
    | "dependency"
    | "operational";
  severity?: "critical" | "high" | "medium" | "low";
  estimated_effort?: "hours" | "days" | "weeks" | "months" | "quarters";
  incurred_date?: string;
  incurred_by?: string;
  reason?: string;
  impact?: string;
  remediation_plan?: string;
  remediation_doc?: string;
  blocked_by?: string[];
  accepted_risk?: boolean;
}

export interface ApiContractFrontmatter extends BaseFrontmatter {
  type: "api-contract";
  api_name?: string;
  version?: string;
  protocol?: "rest" | "grpc" | "graphql" | "websocket" | "event" | "cli";
  stability?: "stable" | "beta" | "alpha" | "experimental" | "deprecated";
  base_url?: string;
  auth_method?: string;
  breaking_change_policy?: string;
  changelog?: Array<{
    version: string;
    date: string;
    changes: string[];
  }>;
  deprecation_notice?: string | null;
}

export interface FeatureBriefFrontmatter extends BaseFrontmatter {
  type: "feature-brief";
  problem_statement?: string;
  hypothesis?: string;
  success_criteria?: string[];
  estimated_size?: "xs" | "s" | "m" | "l" | "xl";
  target_quarter?: string;
  escalates_to_prd?: boolean;
  related_prd?: string | null;
}

export interface DecisionLogFrontmatter extends BaseFrontmatter {
  type: "decision-log";
  context?: string;
  decision?: string;
  alternatives?: string[];
  rationale?: string;
  escalate_to_adr?: boolean;
  related_adr?: string | null;
}

export interface RoadmapSnapshotFrontmatter extends BaseFrontmatter {
  type: "roadmap-snapshot";
}

export interface QuarterlyReviewFrontmatter extends BaseFrontmatter {
  type: "quarterly-review";
}

export interface OnboardingFrontmatter extends BaseFrontmatter {
  type: "onboarding";
}

// ─── Discriminated Union ─────────────────────────────────────────────────────

export type Frontmatter =
  | AdrFrontmatter
  | PrdFrontmatter
  | RfcFrontmatter
  | TddFrontmatter
  | RunbookFrontmatter
  | PostmortemFrontmatter
  | SloFrontmatter
  | ServiceCatalogFrontmatter
  | DeprecationFrontmatter
  | TechDebtFrontmatter
  | ApiContractFrontmatter
  | FeatureBriefFrontmatter
  | DecisionLogFrontmatter
  | RoadmapSnapshotFrontmatter
  | QuarterlyReviewFrontmatter
  | OnboardingFrontmatter;

// ─── Relationship field keys ─────────────────────────────────────────────────

export const RELATIONSHIP_FIELDS = [
  "related",
  "supersedes",
  "superseded_by",
  "implements",
  "motivated_by",
  "part_of",
  "depends_on",
] as const;

export type RelationshipField = (typeof RELATIONSHIP_FIELDS)[number];

// ─── User fields (fields that contain usernames) ─────────────────────────────

export const USER_FIELDS = [
  "author",
  "owner",
  "reviewers",
  "approvers",
] as const;

export const USER_ARRAY_FIELDS = ["reviewers", "approvers"] as const;
export const USER_SINGLE_FIELDS = ["author", "owner"] as const;
